import React, { FC, useContext, useState, useEffect } from "react";
import { useIntl, defineMessages } from "react-intl";
import { Form, Button } from "@natera/form";
import {
  SetValidationError,
  ClearErrors,
  ClearValidationError,
  GetValidationError,
} from "@natera/platform/lib/hooks/useError";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import { Address, Profile } from "@app/provider/profile";
import { ConfigContext } from "@app/provider";
import { ProfileFormAccordion } from "@app/components/forms/profileFormAccordion";
import { MessageDescriptor } from "@formatjs/intl";
import { validateZipCode } from "@app/utils";
import { messages as addressFormMessages } from "@app/components/forms/addressForm/messages";

const messages = defineMessages({
  confirmationFormConfirm: {
    id: "confirmationFormConfirm",
    defaultMessage: "Confirm",
  },
  confirmationFormInvalidPhoneNumber: {
    id: "confirmationFormInvalidPhoneNumber",
    defaultMessage: "Please enter a valid phone number.",
  },
  confirmationFormUSPhoneNumber: {
    id: "confirmationFormUSPhoneNumber",
    defaultMessage: "You must enter a US based number to create an account.",
  },
  confirmationFormRequired: {
    id: "confirmationFormRequired",
    defaultMessage: "This information is required.",
  },
});

interface ConfirmationFormProps {
  isLoading: boolean;
  profile: Profile;
  onSubmit: (profileData: Profile) => void;
  setValidationError: SetValidationError;
  getValidationError: GetValidationError;
  clearValidationError: ClearValidationError;
  clearErrors: ClearErrors;
  setPhone: (phone: string) => void;
}

const ConfirmationForm: FC<ConfirmationFormProps> = ({
  isLoading,
  profile,
  clearErrors,
  clearValidationError,
  onSubmit,
  setValidationError,
  getValidationError,
  setPhone,
}) => {
  const intl = useIntl();
  const { config } = useContext(ConfigContext);

  const DEFAULT_PHONE_NUMBER_LENGTH = config.DEFAULT_PHONE_NUMBER_LENGTH;

  const { validatePhone } = usePhoneValidation();

  const primaryAddress = profile?.addresses?.[0];

  const [names, setNames] = useState({
    firstName: profile?.firstName ?? "",
    middleName: profile?.middleName ?? "",
    lastName: profile?.lastName ?? "",
  });

  const [mobileNumber, setMobileNumber] = useState(profile?.phone ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(profile?.dateOfBirth ?? "");
  const [zipCode, setZipCode] = useState(primaryAddress?.zipCode ?? "");
  const [street1, setStreet1] = useState(primaryAddress?.street1 ?? "");
  const [apartmentUnit, setApartmentUnit] = useState(
    primaryAddress?.street2 ?? ""
  );
  const [city, setCity] = useState(primaryAddress?.city ?? "");
  const [state, setState] = useState(primaryAddress?.state ?? "");

  const isLastNameFromToken = !!profile?.lastName;
  const isDateOfBirthFromToken = !!profile?.dateOfBirth;
  const isAddressFromToken = !!(
    primaryAddress?.street1 ||
    primaryAddress?.state ||
    primaryAddress?.city ||
    primaryAddress?.zipCode
  );
  const isMobileNumberFromToken = !!profile?.phone;

  useEffect(() => {
    if (profile) {
      setNames({
        firstName: profile.firstName ?? "",
        middleName: profile.middleName ?? "",
        lastName: profile.lastName ?? "",
      });
      setMobileNumber(profile.phone ?? "");
      setDateOfBirth(profile.dateOfBirth ?? "");
      setZipCode(primaryAddress?.zipCode ?? "");
      setStreet1(primaryAddress?.street1 ?? "");
      setApartmentUnit(primaryAddress?.street2 ?? "");
      setCity(primaryAddress?.city ?? "");
      setState(primaryAddress?.state ?? "");
    }
  }, [profile]);

  const handleStreet1Change = (value: string) => setStreet1(value);

  const handleNamesChange: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setNames((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));

  const handleNamesUpdate: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setNames((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value.trim(),
    }));

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    clearErrors();

    const firstName = names.firstName.trim();
    const middleName = names.middleName.trim();
    const lastName = names.lastName.trim();

    setPhone(mobileNumber);

    let hasErrors = false;

    const addError = (field: string, messageKey: MessageDescriptor) => {
      setValidationError(field, intl.formatMessage(messageKey));
      hasErrors = true;
    };
    const requiredMessage = messages.confirmationFormRequired;

    if (!firstName) {
      addError("firstName", requiredMessage);
    }

    if (!lastName) {
      addError("lastName", requiredMessage);
    }

    if (!dateOfBirth) {
      const message = intl.formatMessage(requiredMessage);
      setValidationError("dateOfBirthRequired", message);
      hasErrors = true;
    }

    if (!street1?.trim()) {
      addError("street1", requiredMessage);
    }

    if (!city?.trim()) {
      addError("city", requiredMessage);
    }

    if (!state?.trim()) {
      addError("state", requiredMessage);
    }

    const trimmedZipCode = (zipCode ?? "").trim();
    if (!trimmedZipCode) {
      addError("zipCode", requiredMessage);
    } else if (!validateZipCode(trimmedZipCode)) {
      addError("zipCode", addressFormMessages.addressFormInvalidZipCode);
    }

    const phoneValidations = [
      {
        condition: !mobileNumber || mobileNumber.length === 0,
        message: requiredMessage,
      },
      {
        condition: mobileNumber.length < DEFAULT_PHONE_NUMBER_LENGTH,
        message: messages.confirmationFormInvalidPhoneNumber,
      },
      {
        condition: !mobileNumber.startsWith("1"),
        message: messages.confirmationFormUSPhoneNumber,
      },
      {
        condition: !validatePhone(mobileNumber, "US"),
        message: messages.confirmationFormInvalidPhoneNumber,
      },
    ];

    for (const validation of phoneValidations) {
      if (validation.condition) {
        addError("mobileNumber", validation.message);
        break;
      }
    }

    if (hasErrors) {
      return;
    }

    const address: Address = {
      uid: "",
      countryCode: "US",
      city: city.trim(),
      state: state.trim(),
      street1: street1.trim(),
      street2: apartmentUnit.trim(),
      zipCode: trimmedZipCode,
    };

    const profileData: Profile = {
      firstName,
      middleName: middleName || undefined,
      lastName,
      phone: mobileNumber,
      dateOfBirth: dateOfBirth,
      addresses: [address],
    };

    onSubmit(profileData);
  };

  return (
    <Form
      className="setup-profile-form__container"
      noValidate
      onSubmit={handleSubmit}
    >
      <ProfileFormAccordion
        names={names}
        onNamesChange={handleNamesChange}
        onNamesUpdate={handleNamesUpdate}
        dateOfBirth={dateOfBirth}
        onDateOfBirthChange={setDateOfBirth}
        zipCode={zipCode}
        onZipCodeChange={setZipCode}
        street1={street1}
        onStreet1Change={handleStreet1Change}
        apartmentUnit={apartmentUnit}
        onApartmentUnitChange={setApartmentUnit}
        city={city}
        onCityChange={setCity}
        state={state}
        onStateChange={setState}
        mobileNumber={mobileNumber}
        onMobileNumberChange={setMobileNumber}
        currentPhone={profile?.phone}
        getValidationError={getValidationError}
        setValidationError={setValidationError}
        clearValidationError={clearValidationError}
        nameConfig={{
          lastNameReadOnly: isLastNameFromToken,
        }}
        dateOfBirthConfig={{
          readOnly: isDateOfBirthFromToken,
          expanded: isDateOfBirthFromToken,
        }}
        addressConfig={{
          expanded: isAddressFromToken,
        }}
        mobileNumberConfig={{
          readOnly: false,
          expanded: isMobileNumberFromToken,
        }}
        sections={{
          showAlternativeLastName: false,
          showDateOfBirth: true,
          showAddress: true,
          showMobileNumber: true,
        }}
        initialDateOfBirth={profile?.dateOfBirth}
        maxNameLength={255}
        withPadding={true}
      />
      <Button type="submit" unelevated loading={isLoading}>
        {!isLoading && intl.formatMessage(messages.confirmationFormConfirm)}
      </Button>
    </Form>
  );
};

export default ConfirmationForm;
